/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 01-Aug-2026
 * @author      : Vijayakumar S, with Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Communication Management §9 — Review Detail diff
 * Purpose      : Pure diff engine behind Review Detail. Word-level LCS over template
 *                bodies, plus a token-level delta computed against the C4 catalog.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * §9             | 01-Aug-2026 | Vijayakumar S  | Initial creation
 */

import { isKnownToken } from './CommunicationTokenCatalog';

/**
 * WHY THIS IS A REAL DIFF AND NOT TWO PANES OF TEXT
 * -------------------------------------------------
 * Approving a message template makes it live for EVERY tenant at once — there is no
 * per-tenant fork and no staged rollout (D51). The reviewer is the only gate, so the
 * screen's job is to make the actual change impossible to miss.
 *
 * Three decisions follow from that:
 *
 * 1. WORD-level, not line-level. Template bodies are short prose, frequently a single
 *    line. A line diff on a one-line SMS reports "the whole body changed" for a
 *    one-word edit, which is the same as showing nothing.
 *
 * 2. Tokens are diffed SEPARATELY as well as inline. This is the important one. The
 *    highest-consequence edit is not prose — it is a `{{token}}` being dropped, added
 *    or misspelt. Dropping `{{estimate.link}}` from an estimate SMS removes the call to
 *    action for every tenant simultaneously, and it is nearly invisible inside a prose
 *    diff. So `diffTemplates` emits an explicit token delta alongside the word runs.
 *
 * 3. UNKNOWN tokens in the proposed body are surfaced as their own class of finding.
 *    A token that is not in the C4 catalog resolves to nothing at send time — the body
 *    saves clean and the SEND fails in production. That is precisely the defect the
 *    catalog exists to eliminate, and the reviewer is the last person who can catch it.
 *
 * Everything here is pure and synchronous so it can be tested without a DOM.
 */

export type DiffOp = 'equal' | 'add' | 'remove';

export interface DiffRun {
  op: DiffOp;
  /** The words in this run, already joined with single spaces for display. */
  value: string;
}

export interface TokenDelta {
  /** Catalogued tokens present in proposed but not in current. */
  added: string[];
  /** Catalogued tokens present in current but not in proposed. */
  removed: string[];
  /** Tokens in the PROPOSED body that are not in the C4 catalog — these resolve to nothing. */
  unknownInProposed: string[];
}

export interface TemplateDiff {
  runs: DiffRun[];
  tokens: TokenDelta;
  wordsAdded: number;
  wordsRemoved: number;
  /** True when current and proposed bodies are byte-identical. */
  identical: boolean;
  /**
   * True when the bodies were too large for the O(n*m) LCS and a cheaper line-level
   * comparison was substituted. Surfaced in the UI so a coarse diff is never mistaken
   * for a precise one.
   */
  degraded: boolean;
}

/**
 * Guard on the LCS table. Template bodies are short by nature (SMS ~160 chars, email
 * bodies rarely past a few hundred words); this only trips on pathological input, and
 * when it does we say so via `degraded` rather than freezing the reviewer's tab.
 */
export const MAX_LCS_WORDS = 1500;

/**
 * MUST stay byte-for-byte equivalent to the server's parser, which is the single
 * definition of "what counts as a placeholder":
 *
 *     SmsTemplatePlaceholderUtil.PLACEHOLDER_PATTERN
 *         = Pattern.compile("\\{\\{([a-zA-Z0-9_.]+)\\}\\}")
 *
 * NOTE THE ABSENCE OF `\s*`. `{{ client.city }}` with inner spaces is NOT a placeholder
 * to the server — it is literal text, left alone by both the validator and the renderer,
 * and it ships to the customer as the raw string `{{ client.city }}`.
 *
 * An earlier draft of this regex tolerated inner whitespace. That would have made the UI
 * report such a body as valid and its tokens as catalogued, while the send rendered the
 * braces literally — precisely the authoring-time-says-fine / production-fails defect the
 * C4 catalog exists to eliminate. Do not "relax" this pattern.
 */
const TOKEN_PATTERN = /\{\{([a-zA-Z0-9_.]+)\}\}/g;

/**
 * Splits on whitespace. `{{client.firstName}}` survives as a single atom because a
 * token key never contains a space, so no special-casing is needed here.
 */
export const tokenizeWords = (text: string | null | undefined): string[] => {
  if (text == null) return [];
  const trimmed = text.trim();
  return trimmed === '' ? [] : trimmed.split(/\s+/);
};

/** Every `{{token}}` key in the text, in order, de-duplicated. */
export const extractTokens = (text: string | null | undefined): string[] => {
  if (text == null) return [];
  const found: string[] = [];
  // matchAll would need a downlevelIteration flag under this tsconfig target; exec loop
  // is equivalent and avoids changing compiler options for one call site.
  TOKEN_PATTERN.lastIndex = 0;
  let match = TOKEN_PATTERN.exec(text);
  while (match !== null) {
    if (!found.includes(match[1])) found.push(match[1]);
    match = TOKEN_PATTERN.exec(text);
  }
  return found;
};

/** The token-level comparison — see decision 2 in the header. */
export const diffTokens = (
  currentBody: string | null | undefined,
  proposedBody: string | null | undefined,
): TokenDelta => {
  const before = extractTokens(currentBody);
  const after = extractTokens(proposedBody);
  return {
    added: after.filter((t) => !before.includes(t)),
    removed: before.filter((t) => !after.includes(t)),
    unknownInProposed: after.filter((t) => !isKnownToken(t)),
  };
};

/** Collapses a word-op sequence into display runs, merging consecutive same-op words. */
const toRuns = (ops: Array<{ op: DiffOp; word: string }>): DiffRun[] => {
  const runs: DiffRun[] = [];
  ops.forEach(({ op, word }) => {
    const tail = runs[runs.length - 1];
    if (tail && tail.op === op) {
      tail.value = `${tail.value} ${word}`;
    } else {
      runs.push({ op, value: word });
    }
  });
  return runs;
};

/**
 * Standard LCS backtrack over word arrays. O(n*m) time and space, bounded by
 * MAX_LCS_WORDS at the call site.
 */
const lcsWordOps = (before: string[], after: string[]): Array<{ op: DiffOp; word: string }> => {
  const n = before.length;
  const m = after.length;

  // table[i][j] = LCS length of before[i..] and after[j..]
  const table: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      table[i][j] =
        before[i] === after[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  const ops: Array<{ op: DiffOp; word: string }> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (before[i] === after[j]) {
      ops.push({ op: 'equal', word: before[i] });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      ops.push({ op: 'remove', word: before[i] });
      i += 1;
    } else {
      ops.push({ op: 'add', word: after[j] });
      j += 1;
    }
  }
  while (i < n) {
    ops.push({ op: 'remove', word: before[i] });
    i += 1;
  }
  while (j < m) {
    ops.push({ op: 'add', word: after[j] });
    j += 1;
  }
  return ops;
};

/** Coarse fallback for pathologically long bodies — whole-body replace, flagged degraded. */
const degradedOps = (before: string[], after: string[]): Array<{ op: DiffOp; word: string }> => [
  ...before.map((word) => ({ op: 'remove' as DiffOp, word })),
  ...after.map((word) => ({ op: 'add' as DiffOp, word })),
];

/** Word-level diff of two bodies. */
export const diffWords = (
  currentBody: string | null | undefined,
  proposedBody: string | null | undefined,
): { runs: DiffRun[]; degraded: boolean } => {
  const before = tokenizeWords(currentBody);
  const after = tokenizeWords(proposedBody);
  const degraded = before.length > MAX_LCS_WORDS || after.length > MAX_LCS_WORDS;
  const ops = degraded ? degradedOps(before, after) : lcsWordOps(before, after);
  return { runs: toRuns(ops), degraded };
};

/**
 * The full comparison Review Detail renders.
 *
 * `currentBody` is null when there is no active version to compare against — a brand-new
 * template (version 1). That is a legitimate, fully-renderable state: every word is an
 * addition and the token delta is "all tokens added". It is NOT the same as "the current
 * version could not be loaded", which the caller must handle before reaching this.
 */
export const diffTemplates = (
  currentBody: string | null | undefined,
  proposedBody: string | null | undefined,
): TemplateDiff => {
  const { runs, degraded } = diffWords(currentBody, proposedBody);
  return {
    runs,
    tokens: diffTokens(currentBody, proposedBody),
    wordsAdded: runs.filter((r) => r.op === 'add').reduce((n, r) => n + tokenizeWords(r.value).length, 0),
    wordsRemoved: runs.filter((r) => r.op === 'remove').reduce((n, r) => n + tokenizeWords(r.value).length, 0),
    identical: (currentBody ?? '') === (proposedBody ?? ''),
    degraded,
  };
};

/** One-line human summary for the top of Review Detail. */
export const summariseDiff = (diff: TemplateDiff): string => {
  if (diff.identical) return 'No change to the body.';
  const parts = [`${diff.wordsAdded} word${diff.wordsAdded === 1 ? '' : 's'} added`,
    `${diff.wordsRemoved} removed`];
  if (diff.tokens.added.length > 0) parts.push(`${diff.tokens.added.length} token(s) added`);
  if (diff.tokens.removed.length > 0) parts.push(`${diff.tokens.removed.length} token(s) removed`);
  return `${parts.join(', ')}.`;
};
